import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, CheckCircle, Users, UserPlus } from 'lucide-react';
import StudentForm from '../../components/forms/StudentForm';
import Button from '../../components/ui/Button';
import { useAuth } from '../../contexts/AuthContext';
import { dataHelpers, supabase } from '../../lib/supabase';
import toast from 'react-hot-toast';

const StudentRegisterPage = () => {
  const navigate = useNavigate();
  const { company } = useAuth();
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (studentData) => {
    if (!company?.id) {
      toast.error('Empresa não encontrada');
      return;
    }

    try {
      setLoading(true);
      
      // Garantir que person_id e financial_parent_id sejam enviados
      const dataToSend = {
        ...studentData,
        person_id: studentData.selected_parent_id || null,
        financial_parent_id: studentData.selected_financial_parent_id || null,
      };

      console.log('Creating student with data:', dataToSend);

      // Criar aluno usando dataHelpers (que também criará o usuário)
      const createdStudent = await dataHelpers.createStudent(company.id, dataToSend);
      
      console.log('Student created successfully:', createdStudent);
      
      // Se há saldo inicial, criar transação financeira
      const initialBalance = parseFloat(dataToSend.balance || 0);
      console.log('🔍 Debug saldo inicial:', {
        balance: dataToSend.balance,
        parsed: initialBalance,
        shouldCreateTransaction: initialBalance > 0
      });
      
      if (initialBalance > 0) {
        console.log('💰 Criando transação de saldo inicial:', initialBalance);
        
        try {
          const transactionData = {
            id: crypto.randomUUID(),
            company_id: company.id,
            student_id: createdStudent.id,
            type: 'credit',
            transaction_type: 'credit',
            description: `Saldo inicial - ${createdStudent.name}`,
            amount: initialBalance,
            balance_before: 0,
            balance_after: initialBalance,
            payment_method: 'initial_balance',
            details: {
              initial_balance: true,
              student_name: createdStudent.name,
              created_via: 'STUDENT_REGISTRATION'
            },
            status: 'completed',
            created_by: createdStudent.financial_parent_id || '00000000-0000-0000-0000-000000000000',
            created_at: new Date().toISOString()
          };
          
          console.log('📝 Dados da transação a ser inserida:', transactionData);
          
          const { data: insertResult, error: transactionError } = await supabase
            .from('financial_transactions')
            .insert(transactionData)
            .select()
            .single();
          
          console.log('📊 Resultado da inserção:', { insertResult, transactionError });
          
          if (transactionError) {
            console.error('❌ Erro ao criar transação de saldo inicial:', transactionError);
            toast.error('Aluno criado, mas houve erro ao registrar saldo inicial');
          } else {
            console.log('✅ Transação de saldo inicial criada com sucesso:', insertResult);
            toast.success('Aluno cadastrado com sucesso e saldo inicial registrado!');
          }
        } catch (transactionError) {
          console.error('Erro ao criar transação de saldo inicial:', transactionError);
          toast.error('Aluno criado, mas houve erro ao registrar saldo inicial');
        }
      } else {
        toast.success('Aluno cadastrado com sucesso!');
      }
      
      // Redirecionar para a lista de alunos
      navigate('/dashboard/students', { 
        state: { 
          message: 'Aluno cadastrado com sucesso!',
          studentId: createdStudent.id 
        }
      });
    } catch (error) {
      console.error('Error creating student:', error);
      console.error('Error details:', {
        message: error.message,
        stack: error.stack,
        studentData: {
          active: studentData.active,
          email: studentData.email,
          hasPassword: !!studentData.password
        }
      });
      
      let errorMessage = 'Erro ao cadastrar aluno';
      
      if (error.message?.includes('duplicate')) {
        errorMessage = 'Já existe um aluno com este email ou matrícula';
      } else if (error.message?.includes('registration_number')) {
        errorMessage = 'Número de matrícula já está em uso';
      } else if (error.message?.includes('already registered')) {
        errorMessage = 'Este email já está cadastrado no sistema';
      } else if (error.message?.includes('invalid email')) {
        errorMessage = 'Email inválido';
      } else if (error.message?.includes('password')) {
        errorMessage = 'Problema com a senha (mínimo 6 caracteres)';
      } else if (error.message?.includes('rate limit')) {
        errorMessage = 'Muitas tentativas. Aguarde alguns minutos';
      } else if (error.message?.includes('company_id')) {
        errorMessage = 'Erro de autorização. Verifique se você tem permissão para cadastrar alunos';
      } else if (error.message?.includes('auth')) {
        errorMessage = 'Erro ao criar conta de acesso: ' + error.message.split(':').pop();
      } else if (error.message) {
        errorMessage = error.message;
      }
      
      toast.error(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const handleCancel = () => {
    navigate('/dashboard/students');
  };

  return (
    <>
      <div className="space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-4">
            <Button
              variant="outline"
              onClick={() => navigate('/dashboard/students')}
              className="flex items-center"
            >
              <ArrowLeft className="w-4 h-4 mr-2" />
              Voltar
            </Button>
            <div>
              <h1 className="text-2xl font-bold text-gray-900">Cadastrar Aluno</h1>
              <p className="text-gray-600">
                Adicione um novo aluno ao sistema da cantina
              </p>
            </div>
          </div>
        </div>

        {/* Informações da empresa */}
        {company && (
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
            <div className="flex items-center">
              <CheckCircle className="w-5 h-5 text-blue-600 mr-2" />
              <span className="text-blue-800">
                Cadastrando aluno para: <strong>{company.name}</strong>
              </span>
            </div>
          </div>
        )}

        {/* Formulário */}
        <StudentForm
          onSubmit={handleSubmit}
          onCancel={handleCancel}
          loading={loading}
        />

        {/* Dicas */}
        <div className="bg-gray-50 border border-gray-200 rounded-lg p-6">
          <h3 className="text-lg font-medium text-gray-900 mb-4">💡 Dicas para cadastro de alunos</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 text-sm text-gray-600">
            <div>
              <h4 className="font-medium text-gray-900 mb-2">Dados do Aluno</h4>
              <ul className="space-y-1">
                <li>• Use o nome completo do aluno</li>
                <li>• Email deve ser único no sistema</li>
                <li>• Matrícula deve seguir padrão da escola</li>
                <li>• Vincule à turma correta</li>
              </ul>
            </div>
            <div>
              <h4 className="font-medium text-gray-900 mb-2">Responsáveis</h4>
              <ul className="space-y-1">
                <li>• Dados completos para contato</li>
                <li>• Email para notificações automáticas</li>
                <li>• Telefone sempre atualizado</li>
                <li>• CPF para identificação</li>
              </ul>
            </div>            <div>
              <h4 className="font-medium text-gray-900 mb-2">Acesso ao Sistema</h4>
              <ul className="space-y-1">
                <li>• Acesso criado apenas para alunos ativos</li>
                <li>• Email e senha são necessários para acesso</li>
                <li>• Alunos inativos ficam apenas cadastrados</li>
                <li>• Status pode ser alterado posteriormente</li>
              </ul>
            </div>
            <div>
              <h4 className="font-medium text-gray-900 mb-2">Configurações Financeiras</h4>
              <ul className="space-y-1">
                <li>• Escolha o tipo de pagamento</li>
                <li>• Defina saldo inicial se necessário</li>
                <li>• Configure limite de crédito</li>
                <li>• Monitore movimentações</li>
              </ul>
            </div>
          </div>
        </div>

        {/* Informações sobre tipos de pagamento */}
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-6">
          <h3 className="text-lg font-medium text-blue-900 mb-4">📋 Tipos de Pagamento</h3>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 text-sm">
            <div className="bg-white p-4 rounded-lg border border-blue-200">
              <h4 className="font-medium text-blue-900 mb-2">💳 Pré-pago (Recarga)</h4>
              <ul className="space-y-1 text-blue-700">
                <li>• Responsável faz recargas</li>
                <li>• Saldo descontado nas compras</li>
                <li>• Controle total dos gastos</li>
                <li>• Sem débitos em atraso</li>
              </ul>
            </div>
            <div className="bg-white p-4 rounded-lg border border-blue-200">
              <h4 className="font-medium text-blue-900 mb-2">🏪 Crédito (Pós-pago)</h4>
              <ul className="space-y-1 text-blue-700">
                <li>• Compras até o limite</li>
                <li>• Cobrança posterior</li>
                <li>• Flexibilidade nas compras</li>
                <li>• Controle de inadimplência</li>
              </ul>
            </div>
            <div className="bg-white p-4 rounded-lg border border-blue-200">
              <h4 className="font-medium text-blue-900 mb-2">📅 Mensalista</h4>
              <ul className="space-y-1 text-blue-700">
                <li>• Valor fixo mensal</li>
                <li>• Consumo sem limites</li>
                <li>• Ideal para uso regular</li>
                <li>• Previsibilidade de custos</li>
              </ul>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default StudentRegisterPage;